/**
 * Pragmatic widget locator for the HFCL Chat Bot.
 *
 * Used when the user asks for something by visible title — a graph name
 * ("Active QOE", "Top Performing Sites"), an accordion ("All Sites",
 * "All Access Points"), or any other titled card on a dashboard page.
 *
 * Strategy:
 *   1. Walk every visible text node on the page and find one whose
 *      trimmed text matches the requested name (case-insensitive).
 *   2. From that text node, walk UP the DOM until we find a "card-like"
 *      ancestor (large enough to look like a widget/accordion).
 *   3. Scroll that ancestor smoothly into view.
 *   4. Pulse a temporary blue outline + soft glow on it for ~2s so the
 *      user can see exactly what the chat opened.
 *
 * Exposed as `window.hfclScrollToWidget(name)` so the chat's
 * `triggerPostNavAction` can call it via a pseudo-action.
 */

const HIGHLIGHT_MS = 2000;

const normalise = (text) =>
	String(text || '')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();

const isCardLike = (el) => {
	if (!el || el === document.body || el === document.documentElement) return false;
	const rect = el.getBoundingClientRect();
	return rect.height >= 80 && rect.width >= 160;
};

const isAccordionSummary = (el) =>
	el &&
	el.getAttribute &&
	el.getAttribute('role') === 'button' &&
	el.hasAttribute('aria-expanded');

// MUI's AccordionSummary keeps `aria-expanded` on the root; custom accordions
// sometimes don't, so also look for any clickable header-like ancestor.
// Used as a fallback when isAccordionSummary doesn't find anything.
//
// Critical: do NOT match MUI subelement classes like MuiAccordionSummary-content,
// -expandIconWrapper, or -gutters — those are children of the real summary and
// would cause findCardAncestor to stop one level too low.
const isCollapsibleHeader = (el) => {
	if (!el) return false;
	const cls = typeof el.className === 'string' ? el.className : '';
	if (!cls) return false;
	// Reject MUI's accordion subelements outright.
	if (/Mui[A-Za-z]*Summary-(content|expandIconWrapper|gutters)/.test(cls)) {
		return false;
	}
	// Accept MUI root or custom accordion header / toggle classes.
	return (
		/MuiAccordionSummary-root\b/.test(cls) ||
		/(?:^|[\s_-])accordion[-_]?(?:header|toggle)\b/i.test(cls) ||
		/(?:^|[\s_-])panel[-_]?(?:header|toggle)\b/i.test(cls)
	);
};

const findCardAncestor = (node) => {
	const BUDGET = 12;

	// Pass 1: the strongest signal — an MUI AccordionSummary root, identified
	// by `role="button"` + `aria-expanded`. Walking the whole budget here
	// ensures we don't bail out early on an intermediate wrapper (e.g. the
	// MuiAccordionSummary-content div, or a 100%-wide flex row inside the
	// summary that would otherwise match isCardLike).
	let el = node?.parentElement || null;
	for (let i = 0; i < BUDGET && el; i += 1) {
		if (isAccordionSummary(el)) return el;
		el = el.parentElement;
	}

	// Pass 2: custom accordion header without aria-expanded.
	el = node?.parentElement || null;
	for (let i = 0; i < BUDGET && el; i += 1) {
		if (isCollapsibleHeader(el)) return el;
		el = el.parentElement;
	}

	// Pass 3: not an accordion at all — fall back to the nearest visually
	// card-like ancestor (graphs, plain widgets, etc.).
	el = node?.parentElement || null;
	let last = node;
	for (let i = 0; i < BUDGET && el; i += 1) {
		if (isCardLike(el)) return el;
		last = el;
		el = el.parentElement;
	}
	return last;
};

/**
 * Dispatch a full mouse-event sequence (mousedown → mouseup → click).
 * MUI's ButtonBase wires its expand handler on pointer/mouse events and a
 * bare `el.click()` occasionally fails to trigger MUI's internal pointer
 * chain. This is the reliable way to "press" any react/MUI button.
 *
 * @param {Element} el the element to click
 * @return {boolean} true if at least one event was dispatched
 */
const robustClick = (el) => {
	if (!el) return false;
	try {
		const opts = { bubbles: true, cancelable: true, view: window };
		el.dispatchEvent(new MouseEvent('mousedown', opts));
		el.dispatchEvent(new MouseEvent('mouseup', opts));
		// Prefer the native click() so React's synthetic handler fires too.
		if (typeof el.click === 'function') el.click();
		else el.dispatchEvent(new MouseEvent('click', opts));
		return true;
	} catch (_) {
		try { el.click(); return true; } catch (_) { return false; }
	}
};

/**
 * After locating a widget card, try to expand it.
 *
 * Order:
 *   1. The card itself is an MUI AccordionSummary → click it to toggle.
 *   2. The card contains a collapsed AccordionSummary → click it.
 *   3. Custom accordion header inside the card → click it.
 *   4. Graph card with a small top-right expand icon → click it.
 *
 * @param {Element} card the widget container or accordion summary
 * @return {boolean} true if a target was clicked (or already expanded)
 */
const clickExpandButton = (card) => {
	if (!card || !card.querySelectorAll) return false;

	// Strategy 1 — the card itself is an accordion summary. Click it to
	// toggle expansion (MUI AccordionSummary handles the click internally).
	// Skip if it's already expanded.
	if (isAccordionSummary(card)) {
		const expanded = card.getAttribute('aria-expanded') === 'true';
		if (!expanded) return robustClick(card);
		return true; // already expanded — caller can proceed with add-* click
	}

	// Strategy 2 — the card CONTAINS an accordion summary. Click the first
	// collapsed one we find.
	const innerSummary = card.querySelector(
		'[role="button"][aria-expanded="false"]'
	);
	if (innerSummary) return robustClick(innerSummary);

	// Strategy 2b — the card already contains an EXPANDED accordion summary;
	// nothing to do, but signal success so chained actions continue.
	const expandedSummary = card.querySelector(
		'[role="button"][aria-expanded="true"]'
	);
	if (expandedSummary) return true;

	// Strategy 2c — custom accordion header without aria-expanded. Look for
	// a header-like wrapper inside the card.
	const customHeader = Array.from(card.querySelectorAll('*')).find(isCollapsibleHeader);
	if (customHeader) return robustClick(customHeader);

	// Strategy 3 — graph card with a small top-right expand icon button.
	// Use a generous top-zone: at least 80 px, but up to 40 % of the card
	// height for very short cards (so the chevron in a 56-px summary still
	// passes), capped so we don't grab buttons from far down on tall cards.
	const cardRect = card.getBoundingClientRect();
	const topZoneBottom = cardRect.top + Math.min(
		200,
		Math.max(80, cardRect.height * 0.4)
	);

	const candidates = Array.from(card.querySelectorAll('button'));
	let best = null;
	let bestRight = -Infinity;
	for (const btn of candidates) {
		const r = btn.getBoundingClientRect();
		if (r.width <= 0 || r.height <= 0) continue;
		if (r.width > 60 || r.height > 60) continue;
		const center = r.top + r.height / 2;
		if (center > topZoneBottom) continue;
		if (r.right > bestRight) {
			bestRight = r.right;
			best = btn;
		}
	}

	if (!best) return false;
	return robustClick(best);
};

const pulseHighlight = (el) => {
	if (!el || !el.style) return;
	const origTransition = el.style.transition;
	const origBoxShadow = el.style.boxShadow;
	const origOutline = el.style.outline;
	el.style.transition = 'box-shadow 0.25s ease, outline 0.25s ease';
	el.style.outline = '2px solid #2563eb';
	el.style.boxShadow = '0 0 0 6px rgba(37, 99, 235, 0.18)';
	setTimeout(() => {
		el.style.boxShadow = origBoxShadow;
		el.style.outline = origOutline;
		// give the transition a moment to play out before clearing it
		setTimeout(() => { el.style.transition = origTransition; }, 300);
	}, HIGHLIGHT_MS);
};

/**
 * Find a card whose title text matches the given name and bring it on
 * screen. Returns true if a match was located and scrolled.
 *
 * @param {string} name title text to look for (case-insensitive)
 * @param {Object} [opts]
 * @param {boolean} [opts.expand] when true, also click the expand icon
 *   inside the card after a short delay so the graph opens in
 *   fullscreen.
 * @return {boolean}
 */
export const scrollToWidget = (name, opts = {}) => {
	if (typeof document === 'undefined') return false;
	const wanted = normalise(name);
	if (!wanted) return false;

	// Look across any element that might wrap a card title. We don't restrict
	// to leaf nodes any more — many titles are rendered as a <label> whose
	// text spans multiple text nodes plus a small count badge (e.g.
	// "All Access Points (12)") which means the title element itself has an
	// element child. Instead, we score every element by how cleanly its
	// trimmed textContent matches and prefer the smallest match.
	const selector = 'h1, h2, h3, h4, h5, h6, p, span, label, strong, div';
	const nodes = document.querySelectorAll(selector);

	let best = null;
	let bestScore = Infinity; // 0=exact, 1=startsWith, 2=contains
	let bestLen = Infinity;

	for (const node of nodes) {
		const rect = node.getBoundingClientRect && node.getBoundingClientRect();
		if (rect && (rect.width === 0 || rect.height === 0)) continue;

		const text = normalise(node.textContent);
		if (!text || !text.includes(wanted)) continue;

		// Cap how much extra text the title can carry — keeps us from
		// matching whole sections that happen to contain the phrase.
		const slack = wanted.length + 40;

		let score;
		if (text === wanted) score = 0;
		else if (text.startsWith(wanted) && text.length <= slack) score = 1;
		else if (text.length <= slack) score = 2;
		else continue;

		// Strictly better score wins. On equal score, prefer shorter text;
		// on equal score AND equal text length, prefer the LATER element in
		// document order — that's the deepest descendant (e.g. the <label>
		// instead of the outer relative-positioned wrapper that has the same
		// textContent). Walking up from the deepest match is what lets
		// findCardAncestor reach the AccordionSummary above it.
		if (
			score < bestScore ||
			(score === bestScore && text.length < bestLen) ||
			(score === bestScore && text.length === bestLen)
		) {
			best = node;
			bestScore = score;
			bestLen = text.length;
			if (score === 0 && text.length === wanted.length) {
				// Keep iterating — there may be an even deeper exact match
				// (e.g. a <span> inside a <label>). Don't break early.
			}
		}
	}

	const titleNode = best;
	if (!titleNode) return false;

	const card = findCardAncestor(titleNode);
	if (!card) return false;

	try {
		card.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
	} catch (_) {
		card.scrollIntoView();
	}
	pulseHighlight(card);

	// If the caller asked for expand, click the top-right icon button
	// inside the card after the smooth scroll has settled. The small
	// delay lets scrollIntoView finish so the button is reliably hittable.
	if (opts && opts.expand) {
		setTimeout(() => clickExpandButton(card), 450);
	}
	return true;
};

/**
 * Convenience wrapper — equivalent to scrollToWidget(name, { expand: true }).
 *
 * @param {string} name title text to look for (case-insensitive)
 * @return {boolean}
 */
export const expandWidget = (name) => scrollToWidget(name, { expand: true });

/**
 * Expose `window.hfclScrollToWidget` so the chat's pseudo-action runner
 * can invoke it. Idempotent — safe to call multiple times.
 *
 * @return {void}
 */
export const installScrollToWidget = () => {
	if (typeof window === 'undefined') return;
	if (window.hfclScrollToWidget !== scrollToWidget) {
		window.hfclScrollToWidget = scrollToWidget;
	}
	if (window.hfclExpandWidget !== expandWidget) {
		window.hfclExpandWidget = expandWidget;
	}
};
